import React, { useEffect, useState } from "react";
import "../../styles/components/consent/CookieBanner.css";
import "../../styles/components/consent/CookieSettings.css";
import { useLang } from "../../context/LangContext";
import CookieSettings from "./CookieSettings";

const STORAGE_KEY = "kene_cookie_consent_v1";

const DEFAULT_PREFERENCES = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

function normalizePreferences(raw) {
  return {
    necessary: true,
    preferences: Boolean(raw?.preferences),
    analytics: Boolean(raw?.analytics),
    marketing: Boolean(raw?.marketing),
  };
}

function saveConsent(preferences) {
  const normalized = normalizePreferences(preferences);

  const payload = {
    version: 1,
    savedAt: new Date().toISOString(),
    preferences: normalized,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

  window.dispatchEvent(
    new CustomEvent("cookie-consent-updated", {
      detail: normalized,
    })
  );

  return normalized;
}

export default function CookieBanner() {
  const { dict } = useLang();
  const cookieDict = dict?.cookie ?? {};

  const text = {
    title: cookieDict.bannerTitle ?? "Çerez Tercihleri",
    description:
      cookieDict.bannerDescription ??
      "Kene Teknoloji olarak web sitemizde size daha iyi bir deneyim sunabilmek için çerezler kullanıyoruz. Bu çerezler sitenin temel çalışmasını sağlar, tercihlerinizi hatırlar ve onay vermeniz halinde analitik/pazarlama amaçlı kullanılabilir.",
    acceptAll: cookieDict.acceptAll ?? "Kabul Et",
    rejectOptional: cookieDict.rejectOptional ?? "Reddet",
    settings: cookieDict.settings ?? "Ayarlar",
  };

  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (!raw) {
        setVisible(true);
        return;
      }

      const parsed = JSON.parse(raw);
      const normalized = normalizePreferences(parsed?.preferences);
      setPreferences(normalized);
      setVisible(false);
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const next = saveConsent({
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    });

    setPreferences(next);
    setVisible(false);
    setSettingsOpen(false);
  };

  const handleRejectOptional = () => {
    const next = saveConsent({
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
    });

    setPreferences(next);
    setVisible(false);
    setSettingsOpen(false);
  };

  const handleSaveSettings = (values) => {
    const next = saveConsent(values);
    setPreferences(next);
    setVisible(false);
    setSettingsOpen(false);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  return (
    <>
      {visible && (
        <div
          className="cookie-banner"
          role="dialog"
          aria-live="polite"
          aria-label={text.title}
        >
          <div className="cookie-banner__inner">
            <div className="cookie-banner__content">
              <h3 className="cookie-banner__title">{text.title}</h3>
              <p className="cookie-banner__description">{text.description}</p>
            </div>

            <div className="cookie-banner__actions">
              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--ghost"
                onClick={handleOpenSettings}
              >
                {text.settings}
              </button>

              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--soft"
                onClick={handleRejectOptional}
              >
                {text.rejectOptional}
              </button>

              <button
                type="button"
                className="cookie-banner__btn cookie-banner__btn--primary"
                onClick={handleAcceptAll}
              >
                {text.acceptAll}
              </button>
            </div>
          </div>
        </div>
      )}

      <CookieSettings
        open={settingsOpen}
        values={preferences}
        onClose={handleCloseSettings}
        onSave={handleSaveSettings}
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectOptional}
      />
    </>
  );
}